
export interface TheoryChapter {
  id: string;
  title: string;
  desc: string;
  color: string;
  content: string;
}

export const theoriesData: TheoryChapter[] = [
  {
    id: 'basics',
    title: '基础与四把钥匙',
    desc: '阴盘奇门底层逻辑、四纲取用、四害详解（击刑/门迫/空亡/入墓）',
    color: 'bg-slate-900 text-white',
    content: `# 奇门基础与四把钥匙

---

## 阴盘与阳盘

### 时间周期的差异

奇门遁甲自古有阴盘与阳盘两种推演体系，二者的根本差异在于**时间周期的取法不同**。

阴盘奇门以**月亮绕地球**一周为基准，其节奏更贴近日常生活的变化频率。在阴盘体系中，**时局**以年月日时四柱起盘，每两小时换一局，一日十二时辰即十二局，细腻地捕捉昼夜交替中的气场流转。若用**刻局**，则每十分钟一局，精度更高，适用于需要精细择时的事项。阴盘重视**象意**与**通气**，不拘泥于传统吉凶神煞的简单标注，断卦时直指符号组合与宫位关系，通过「读象」来揭示事物的本质，因而在实战中更为灵活、也更注重当下时空的独特性。

阳盘奇门则以**太阳在黄道十二宫**的运行周期为基准，大约五天换一局，更偏重长期趋势与节气轮转。阳盘在传统典籍中应用广泛，尤其在军事、择日等领域，但其节奏较慢，对于日常生活中「此时此刻」的精准把握，往往不如阴盘敏锐。

### 四纲的共同前提

无论阴盘还是阳盘，有一个共同的前提：**四纲**——年、月、日、时四柱的天干——是整个预测的坐标系。人在何处，神在何处；事在何处，象在何处。四纲不清，则用神必乱；用神一乱，断卦全盘皆输。因此，学习奇门的第一步，不是背诵吉凶格，而是彻底理解四纲的含义与用法。

---

## 四纲与取用神

### 四纲的含义

四纲即四柱的天干：年干、月干、日干、时干。这四个天干在奇门局中各有固定的象意指向，是取用神的根本依据。

| 四柱 | 代表 | 层面 |
|------|------|------|
| 年干 | 长辈、大领导、大环境、国家政策、行业趋势 | 天 |
| 月干 | 兄弟、同事、竞争对手、直接领导、同辈 | 同 |
| 日干 | 求测人本人（当面测） | 我 |
| 时干 | 事体、晚辈、下属、平台、未来、所问之事 | 事 |

**年干**是最高、最外、最不可控的因素。在职场预测中，年干宫与日干宫的关系，直接反映求测人与上级、与宏观环境是否契合；在婚姻预测中，年干可代表父母、家族对婚姻的态度；在疾病预测中，年干有时代表久病、陈年病灶。

**月干**代表同侪、对手、合作伙伴。当面测时，日干为求测人，月干则为与之平行的他人；**电话测、网测时，求测人不在现场，则以月干为求测人**，因为月干代表「与我同辈、与我联系」的那个主体。这是阴盘的重要规则：人在面前看日干，人不在面前看月干。许多初学者把网测也当日干看，结果断出来的是预测师自己的状态，自然牛头不对马嘴。

**日干**代表求测人本人，但仅限**当面测**。当求测人坐在你面前，时空同频，日干就是他的信息载体。日干落宫的状态、符号、四害，直接反映其当下的处境、心态、能力。日干是预测的核心锚点。

**时干**代表所问之事的发展趋势。无论预测婚姻、事业、疾病还是其他，时干宫都承载着「这件事会怎样」「这件事现在处于什么状态」的信息。时干与日干的关系——相生、相克、比和——是判断成败的关键：事生人则顺，人克事则能掌控，事克人则压力大，人生事则需付出。

### 阴阳与取用神的补充

十天干分阴阳：**阳干**为甲、丙、戊、庚、壬；**阴干**为乙、丁、己、辛、癸。在婚姻预测中，男女阴阳配位影响感情格局；在性格判断中，阳干多主外显、刚健，阴干多主内敛、柔顺。

除四纲外，取用神还可按**类象**：工作看开门，生意看生门，情感看六合，变动看马星，领导看值符，灵异看腾蛇，合伙看六合，敌人看庚，老师看天辅，学生看天芮。按**六亲**：父母、兄弟、妻财、子孙等，需结合四柱与卦象对应。按**宫位**或**方向**：若求测人问某方向如何，直接取该方向对应的宫位。多种取法可交叉验证，信息一致则把握大，信息冲突则需辨析主次。

### 年命

年命即出生年的天干。记法：出生年尾数0为庚，1为辛，2为壬，3为癸，4为甲，5为乙，6为丙，7为丁，8为戊，9为己。当面测、电话测均可兼看年命落宫，年命代表一个人的根本气数、先天禀赋。当日干与年命不一致时，可综合参考；当二者同宫或相生时，信息更集中；当二者相克或分离时，可能主内心与当下处境有冲突。

---

## 四把钥匙（四害）

### 总论

阴盘奇门断卦有一条底线规则：**宫位遇四害，能量受损，往坏了断；无四害，往好了断。** 四害是判断宫位吉凶的第一道门槛。

四害指的是：**击刑、门迫、空亡、入墓**。击刑与门迫各造成约50%的能量损失，空亡与入墓各造成约80%的能量损失。四害的本质：击刑是「自伤」——内部牵扯、别扭、破损；门迫是「被克」——能力发挥受阻、被人拆台；空亡是「转移」——能量不在此地，转移到了别处；入墓是「隐藏」——被收起来、退居二线、难以发挥。

### 击刑

#### 来源与口诀

击刑源于**地支相刑**：子卯相刑、寅巳申三刑、丑未戌三刑、辰午酉亥自刑。天干落地支，当某天干落在某宫时，该宫的地支若与该天干所藏地支构成相刑关系，则该天干**击刑**。**丁、丙、乙三奇不存在击刑**。

口诀：「戊三己二庚刑八，辛九壬四癸半八。」谐音：「捂伞挤耳更拧麻花，新酒人鬼都在寺中。」对应：戊在震宫击刑，己在坤宫击刑，庚在艮宫击刑，辛在离宫击刑，壬癸在巽宫击刑。

#### 象意

击刑主**内部问题、牵扯、别扭**，表现为破损、痛楚、自我消耗。各干击刑的象意：壬主迷茫、癸主困难、辛主叛逆、己主垃圾、戊主不进钱、庚主打斗官司口舌。软件排盘中，击刑常用红色斜体标识。

### 门迫

#### 原理与口诀

门迫指的是**八门克九宫**，即「门克宫为破」。口诀：「伤杜木落坤艮土，惊开金克震巽木。景门烈火烧乾兑，生生死死坎水枯。休门进驻离火宫，灰飞烟灭柴无补。」

门迫造成约50%的能量损失。古人云：「吉门门迫吉不起，凶门门破凶不就。」即吉门遇门迫吉性大减，凶门遇门迫凶性也发挥不全，但门迫始终是不利因素。

#### 各门门迫的象意

| 门+宫 | 象意 |
|-------|------|
| 开门落震巽 | 事业无法进行，工作受阻 |
| 惊门落震巽 | 不能发声、宣传受阻、易有谣言诽谤 |
| 伤门落坤艮 | 受伤害、赌博失败、竞争失利 |
| 杜门落坤艮 | 想隐藏的事暴露、技术漏洞、保密失败 |
| 休门落离 | 累、休息不好、调理无效 |
| 生门落坎 | 无法生长、利润难求、发展受阻 |
| 死门落坎 | 死亡、终结，被克则事情僵持 |
| 景门落乾兑 | 前景不好、画面不美、文书合同有问题 |

### 空亡

#### 来源与口诀

空亡源于**六甲旬**。每旬有两个地支「轮空」，口诀：「甲子旬中戌亥空，甲戌旬中申酉空，甲申旬中午未空，甲午旬中辰巳空，甲辰旬中寅卯空，甲寅旬中子丑空。」

空亡造成约80%的能量损失。空亡的意象不是「没有」，而是**转移**——如糖化入水，形虽化而性犹在。空亡之宫，原宫只剩约20%的信息，另外80%转移到**对宫**。断卦时，若用神落空亡，必须**转宫**看对宫。

#### 表现与应期

在排盘软件中，空亡宫位符号旁常出现圆圈标识。空亡主「不起作用、作用转移」——婚姻中可能主异地恋、名存实亡；事业中可能主平台虚、难以立足；疾病中可能主病情转移。

应期：**非填即冲**。填实即四纲中出现该宫地支；冲实即四纲中出现对宫地支。例如戌亥空，到戌亥日时为填实，到辰巳日时为冲实。

### 入墓

#### 口诀与类型

入墓口诀：「甲癸入坤，乙丙戊乾，丁己庚艮，辛壬在巽。」记忆联想：甲癸入坤——假鬼入鲲；乙丙戊乾——一饼捂钱；丁己庚艮——钉鸡更根；辛壬在巽——新人在寻。

入墓造成约80%的能量损失，主**被藏起来、放进仓库、退居二线**。入墓有两种：**时间入墓**（择吉不选入墓日）与**空间入墓**（用神落在入墓之宫）。软件中天干变红常表示入墓。

#### 场景解读与象意延伸

日干入天盘墓：求测人不作为、遇事无能为力、或被囚困、躲藏；年干入墓：上级领导态度不支持；时干入墓：产品积压、员工积极性不高、业绩不达标；开门入墓：无官职或职位不利。应期：冲墓、入墓之时为应，先遇为应。

象意延伸：甲（乌龟、带壳之物）在坤宫为入墓；癸（酒水、鱼缸、鞋子）在坤宫为入墓；乙丙戊（花草、圆形红色、多肉果实）在乾宫为入墓；丁己庚（尖锐、垃圾、金属）在艮宫为入墓；辛壬（钗钏金、饮水机）在巽宫为入墓。地盘三奇入墓主事多暗昧、狐疑不决；天盘三奇入墓主为客事多乖张、遇而不遇。

### 叠加效应

门迫与击刑各损失50%，若同宫并存，剩余能量约25%。再加空亡或入墓，剩余能量骤降至约5%，该宫几乎「瘫痪」。古人云：「宫好往好断，宫不好往坏断。」

### 真假判断

入墓和空亡有「真假」之分。若四纲中出现**本宫地支**或**对宫地支**成对，则为假入墓、假空亡——能量被「拽出来」了。本宫为填，对宫为冲。**必须本宫和对宫的地支成对冲组合**（如辰戌、巳亥）才能拽出；辰亥、巳戌这种交叉组合则不为填实冲实。古人比喻：掉沟里要两个人才能拽出来。

### 反断局

当**四纲天干落宫全坏**时，为**反断局**：宫坏往好断，宫好往坏断。全盘皆「坏」时，往往物极必反，需逆向解读才能贴近实际。

---

## 翻宫

### 看未来

地盘翻天盘。地盘代表基础、过去、隐藏，天盘代表当下、表象、显化。把地盘的天干「翻」到天盘的位置，看其落宫与符号，可推断事情未来的走向。

### 看过去

天盘翻地盘。与看未来相反，把天盘的天干翻到地盘，可追溯事情的来龙去脉、过去的状态。

### 事情选择

时干地盘翻天盘。时干代表第一选择，时干地盘翻到天盘为第二选择，再翻为第三、第四选择。选事物时，先选宫位好的，看与日干生克；三宫都好则看哪个生旺日干落宫；三个都不好则跳过好坏，直接看生克。值符、太阴、开休生所在之宫最佳。

---

## 引干（暗干）

引干是每个宫位中除天盘、地盘、九星、八门、八神之外的隐含信息层，像**药引子、导火索**，反映事物的深层联系、诱发因素、隐藏动因。

- 婚姻预测：配偶宫引干为乙、丙、丁，多主有第三者信息
- 疾病预测：引干常代表**病的起因**，断病先从引干入手
- 事业预测：引干可能代表潜在资源、幕后力量

例如引干戊，可能表示戊（钱）出了问题；在离宫可能对应鼻子；输液针头等尖锐物也可从戊、丁等符号解读。引干是「暗」的层面，往往一语道破天机。

---

## 九宫与宫位结构

### 七个层次

一个完整的奇门宫位，从外到内依次为：

| 层次 | 含义 |
|------|------|
| 宫位 | 方位、外环境、空间（乾西北、坎北、艮东北、震东、巽东南、离南、坤西南、兑西） |
| 八神 | 规律、心态（值符、腾蛇、太阴、六合、白虎、玄武、九地、九天） |
| 天盘 | 表象、外在、公认的形象 |
| 地盘 | 内涵、实质、内心的真实想法 |
| 九星 | 性格、思维（天蓬、天任、天冲、天辅、天英、天芮、天柱、天心） |
| 八门 | 状态（开、休、生、伤、杜、景、死、惊） |
| 引干 | 联系、诱因、深层动因 |

天盘地盘结合可断表里：例如天盘戊、地盘丁壬，可读为外表忠厚、内在复杂；天盘丙、地盘戊，可读为外表热情、内心想你的钱。

### 十二地支与八卦五行

九宫地支：坎子、艮丑寅、震卯、巽辰巳、离午、坤未申、兑酉、乾戌亥。地支与生肖对应：子鼠、丑牛、寅虎、卯兔、辰龙、巳蛇、午马、未羊、申猴、酉鸡、戌狗、亥猪。地支用于判断应期、填实冲实。

九宫源自后天八卦：坎一水、坤二土、震三木、巽四木、中五土、乾六金、兑七金、艮八土、离九火。五行用于判断生克：相生则助，相克则阻，比和则平。五行是奇门断卦的基础语言。

### 内盘与外盘

坎、艮、震、巽为内盘，主近、快、本地；离、坤、兑、乾为外盘，主远、慢、外地。用神在内盘则事来得快，在外盘则来得慢。

---

## 起局与断卦

### 基本逻辑

起局以问事时间的年月日时四柱为准。时局两小时一局，刻局十分钟一局。起局后：首先看四纲落在何宫；其次看四害；再次定用神；最后读象。起局是技术，断局是艺术。

### 象意与读象

阴盘奇门的核心是**象意**。读象原则：**相关性**（所取象意必须与所问之事相关）与**灵活性**（同一符号在不同语境下有不同解读）。象意是开放的，但不可随意；要在实践中积累。

### 通气与共同符号

通气指双方宫位在能量层面是否**对等**、是否有**共同符号**（两宫共同出现的天干）相连。有共同符号说明有联系、能沟通；无则可能「话不投机」。若一方有四害一方无，为不通气，心灵层次不对等。

### 四纲与能量传递

四纲所在之宫能量较强。用神落在四纲宫则受天时影响大；落在四纲以外则可能为次要问题或隐性状态。四纲是「纲」，纲上之宫是重点。

---

## 应期与时间

### 马星

马星主变动、走动、速度。寅午戌马在申，申子辰马在寅，亥卯未马在巳，巳酉丑马在亥。用神临马星主事情动得快。马星所在之宫、之时，往往为事情发生或转折的节点。

### 十二长生

临官、帝旺为旺，能量强；长生次之；墓、绝为弱，能量低。宫位无四害且得长生旺地大吉；有四害又逢墓绝大凶。

### 六甲与旬首

六甲即甲子、甲戌、甲申、甲午、甲辰、甲寅。空亡口诀务必烂熟。起局后先看当日当时属哪一旬，则该旬空亡之宫立即标出。

---

## 常见误区与注意事项

初学易犯误区：**用神取错**（网测用日干）；**忽略四害**；**不会转宫**；**机械读象**；**不会反断**。注意：别人没问的别看、别讲；不做无意义假设；越了解行业算得越准；奇门没错，错在解读。

---

## 与各专题的衔接

本篇所述四纲、四害、翻宫、引干、宫位结构等，是婚姻、事业、择吉、学业、风水、疾病等所有专题预测的**共同基础**。进入具体专题时，需叠加该专题特有的取用神规则和象意侧重。基础不牢，地动山摇。`
  },
  {
    id: 'love',
    title: '情感婚姻预测',
    desc: '单身、恋爱、婚后不同阶段的取用神与断卦',
    color: 'bg-rose-600 text-white',
    content: `# 奇门测情感婚姻教程

---

## 开篇：婚姻预测的阴阳视角

情感婚姻是人生中最为私密也最为复杂的领域。古人云「家和万事兴」，一段稳定的婚姻关系往往决定着一个人的幸福指数与人生走向。阴盘奇门遁甲在婚姻预测方面有着独特优势：它不依赖繁琐的吉凶神煞，而是直指用神落宫与符号组合，通过「象意读解」与「宫位关系」来揭示感情的真实状态。

与阳盘奇门不同，阴盘更重视**象意**与**通气**。所谓通气，指的是双方宫位在能量层面是否对等、是否有共同符号相连。很多时候，两个人表面和谐，却因「不通气」而渐行渐远；也有夫妻吵吵闹闹，却因「同类」而难以分开。理解这一点，是断准婚姻局的基础。

在正式起局断卦之前，有一件事必须先行明确：**求测人处在感情的哪一个阶段？** 找对象、谈恋爱、结婚后、离婚后——这四个阶段的取用神与断法截然不同，若混为一谈，必然失准。

---

## 第一章 选用神：找准坐标系

### 一、四纲取用：人在何处，神在何处

阴盘奇门以**四纲**——年、月、日、时四柱的天干——为整个预测的坐标系。这个坐标系决定了「谁在问事」「事体为何」。

**当面测**时，求测人就在你面前，时空同频，故以**日干**代表求测人本人。**电话测、网测**时，对方不在现场，时空有隔，则以**月干**为用神。这是阴盘的基本规则，不可颠倒。若代他人问测，则按六亲关系：代子女问取时干，代父母问取年干，代兄弟姐妹问取月干。

这里有一个容易忽略的细节：阴盘讲究「人在何处」。当面测时，日干既是人也是事的主轴；远程测时，月干才是那个「正在问事的人」。许多初学者把网测也当日干看，结果断出来的是预测师自己的状态，自然牛头不对马嘴。

### 二、婚姻用神：合干为配偶

婚姻预测的核心用神是**十干合神**。在干支体系中，十天干两两相合，形成五组：甲己、乙庚、丙辛、丁壬、戊癸。这五组合神在婚姻预测中，代表的就是配偶或恋爱对象。

具体对应关系如下：
- **甲己合**——中正之合，甲为领导、强大，己为策划、贵气，组合偏稳重
- **乙庚合**——仁义之合，阳盘奇门主看此对，对方重感情、讲面子，不可令其难堪
- **丙辛合**——威制之合，丙为权力，辛为珠宝，多政治与金钱联姻
- **丁壬合**——淫荡之合，重肉体感觉
- **戊癸合**——无情之合，戊为钱，癸为性，有钱人联姻或娶小三之象

如何确定谁是谁的合干？看求测人的日干（或月干）。若求测人日干为乙，则其配偶或对象为庚；若日干为己，则配偶为甲。以此类推。

### 三、正配、偏配与阴阳颠倒

古人论婚配，讲究阴阳得位。**阳干**为甲、丙、戊、庚、壬；**阴干**为乙、丁、己、辛、癸。男为阳、女为阴，是为正配；男阳女阳或男阴女阴，是为偏配；若男阴女阳，则为阴阳颠倒，古人认为不合。

在实际断卦中，正配未必吉、偏配未必凶，但要心中有数：正配多主感情观念传统、关系相对稳定；偏配则可能一方强势或双方性格相似；阴阳颠倒则容易出现角色错位、沟通不畅。另外需注意：若被测人为女性却临阳干，此时阳干可代表其母亲、姐姐、女儿等女性长辈或同辈，需结合具体问题灵活判断。

### 四、特殊取用规则详解

**甲己同宫**时，表示求测人与配偶的信息重叠在同一宫位，此时伴侣的信息要**转宫**来看，不能直接读本宫。

**离异后问二婚配偶**，不看原来的合干，而是看**合干宫的地盘翻到天盘**，因为二婚对象是「后来的那个人」，地盘代表过去与隐藏，翻出来才是新的缘分。

**多个对象如何区分？** 若求测人依次问「第一个男朋友怎么样」「第二个怎么样」，则第一个看合干，第二个看丁、丙、乙（按此顺序）。为什么是丁丙乙？因为三奇代表异性缘分，丁最吸引眼球、丙易出乱子、乙温文尔雅。若宫内没有丁丙乙，则用合干宫地盘翻天盘的方法。若对象太多无从下手，可建议对方**重新起局**，在新局中第一个问的对应第四个用神。

**直符**有特殊含义：若配偶或对象临直符，直符下所临天干的**合干**往往代表另一段情缘。例如直符下临甲，甲己合，则己可能为另一情人。女人临直符，有时主其人自视甚高、以自我为中心。

**四宫全坏**是一种特殊格局，即年、月、日、时四纲所在宫位均有四害。此时需**反断**：表面看一片凶象，实则关系曲折却未必分手，只是比别人走得更加艰难。

---

## 第二章 四害与宫位关系：婚姻质量的试金石

### 一、四害是什么？为什么如此重要？

四害，指的是**击刑、门迫、空亡、入墓**。它们是阴盘奇门判断宫位能量高低的核心标准。击刑与门迫各损失约50%能量，空亡与入墓则损失约80%。一个宫位若遭遇四害，其内的人事物便处于「受损」状态；若无四害，则能量饱满，可往好了断。

在婚姻预测中，夫妻双方的落宫若有四害，往往对应现实中的问题：击刑主内部别扭、牵扯、痛楚；门迫主能力发挥受阻、彼此拆台；空亡主名存实亡、若即若离；入墓主退居二线、被忽视或隐藏。反之，两宫皆无四害，则根基稳固，纵有摩擦也易化解。

### 二、夫妻宫位的生克比合

找到求测人与配偶的落宫后，要看两宫之间的**五行生克关系**。这里有一个关键：**有无共同符号**。

若两宫**相生**，且有共同天干符号，则生的一方如同父母般关怀备至，被生的一方感到轻松，感情融洽。若无共同符号，则可能是「费力不讨好」——你付出很多，对方却无感，觉得理所当然。

若两宫**相克**，且符号相连，这种克是一种「约束之爱」「负责之爱」，虽然管束多，但彼此有责任感，关系可以维系。若相克且无共同符号，则克的一方容易表现出管制、压迫，被克的一方心生怨恨、恐惧、不快乐，长期下去易生裂痕。

若两宫**比和**（五行相同），有共同符号则主比翼双飞、相互提携；无共同符号则可能是同床异梦，感情淡薄，只是搭伙过日子。

还有一种情况值得注意：两宫相冲相克时，**保持适当距离**反而能维系，天天黏在一起反而矛盾频发。这与现实中的「距离产生美」不谋而合。

**落宫空亡**时必须转宫，因为空亡表示能量转移，原宫只剩20%信息，另外80%在转宫之后的目标宫中。

### 三、共同符号：婚姻的灵魂纽带

共同符号，指的是夫妻两宫之间**共同出现的天干**。有了共同符号，意味着两人有共同话题、共同兴趣、能说到一块去。婚姻中最怕的不是吵架，而是无话可说。很多夫妻离婚，表面是性格不合，实则是缺乏共同语言，心灵无法共鸣。因此，共同符号是判断婚姻质量的重要指标。

### 四、先后天关系：缘分天注定

若夫妻宫位之间存在**先后天关系**（如艮坤、震巽等），则说明这段缘分带有「注定」的色彩，无论顺利与否，都不易轻易分开。遇到矛盾时，这类夫妻往往吵完还能过下去，因为冥冥中有一种力量在维系。

### 五、通气：能量对等的深层含义

通气，是阴盘奇门特有的概念。它指的是双方在能量层面是否**对等**。

若一方宫位有四害、一方没有，叫做**不通气**，两人的心灵层次、思维方式可能不在一个频道，很难真正理解彼此。若两方都有四害，反而是**同类**——要文雅都文雅，要急躁都急躁，反而容易互相理解，关系不易断。若两宫能量都大（无四害或四害少），则婚姻牢固、和谐稳定。

**空亡**在婚姻中常主名存实亡——人在一起，心不在一起。**马星**则主分居、异地恋，或一方常出差、流动性大。

---

## 第三章 六合与情人：婚姻的两面

### 一、六合宫位的多重含义

六合在奇门中代表婚姻、结合、领证。看婚姻必看六合落宫。六合宫对夫妻双方的生克比合，决定了这段婚姻对谁有利、对谁不利。谁克六合，谁对结婚这件事有抗拒心理；六合生谁，谁更渴望成家。

六合临**天芮**，婚姻本身毛病多，可能是双方观念冲突，或外界干扰大。六合临**死门**逢空，若已结婚则关系不好但还能维持；若未婚则难以结成，总有各种阻碍。

**癸**在婚姻中代表性生活。癸落宫若遇空亡、击刑等，往往主性生活不和谐，这也是许多夫妻矛盾的隐性根源。

### 二、情人信息的识别

阴盘奇门对「第三者」的识别有独到方法。**引干**（又称暗干）和**地盘**所临的乙、丙、丁，均可视为情人信息。情人要转到天盘看具体情况。

丁、丙、乙的顺序有讲究：丁最吸引眼球，代表最耀眼的那位；丙易出乱子，代表可能引发风波的情缘；乙温文尔雅，代表较为隐蔽或温和的异性缘。

若配偶落宫遇**直符**，直符下临天干的合干，往往是另一情人。例如直符下临甲，甲己合，己即为另一段情缘。女人临直符，有时断其「自以为是」，在感情中较为自我。

### 三、寄宫与伏吟的特殊解读

**寄宫**表示一个宫位内有两个地支，对应人事则可能为「双重身份」。若对象的宫位有寄宫，且其排在第一位，则可能是「爱你也爱别人」；若排第二位，则可能是爱上了有夫之妇或有妇之夫。

**伏吟**时，乙、丙、丁作为情人符号的用法有所变化，此时以**引干**为情人信息，即便引干不是乙丙丁，也可能代表暗中的情缘。

**日主与情人对宫**，即两人落宫相对（如一个在坎一个在离），这种格局容易「撞破」——事情易暴露，难以隐瞒。

情人宫若**空亡**，则这段情缘断断续续，时有时无。

---

## 第四章 读象与符号组合：从抽象到具体

### 一、天芮星落在谁身上

天芮星代表问题、毛病、病灶。在婚姻预测中，天芮落在谁的宫位，婚姻的问题就主要在谁身上。若天芮落在六合宫，则问题在婚姻本身——不是某一方的错，而是这段关系从根上就有问题。也可以看谁宫位有四害、谁能量低，往往问题多出在能量低的那一方。

### 二、伏吟与反吟：古人为何说「泪水淋淋」

**伏吟**指星、门或天干在本宫不动，主静、主止。在婚姻中，伏吟表示求测人内心忐忑、纠结、难受，却难以行动。未婚者遇伏吟，找对象曲折，易晚婚；已婚者遇伏吟，往往会有再婚、多婚的倾向，第一次婚姻易以分离收场。

**反吟**指对宫相冲，主动、主变。婚姻遇反吟，主反复折腾——先分手再复合，再分手再复合，来回反复，但最终趋势仍是朝着分手方向发展。古人说「反吟伏吟，泪水淋淋」，正是形容这种让人心力交瘁的状态。

### 三、婚姻突变的符号

马星、壬、癸、天冲、九天等符号，主变动迅速。若夫妻宫或六合宫出现这些符号，需警惕婚姻关系可能发生突变——无论是突然好转还是突然恶化，速度都会比较快。

### 四、年龄与性格的落宫判断

对象落**时干**宫，多主年龄较小或性格幼稚；落**月干**宫，多主同龄人；落**年干**宫，多主成熟或年龄较大。结合具体宫位的天干、九星、八门，可进一步推断对方的外貌、职业、性格特征。

### 五、吵架时的心理机制

从宫位生克可以推断吵架时的心理：两宫比合，吵架时往往希望对方「向我看齐」；我克他，容易表现出强迫、无理；他克我，则感到被约束、不满；我生他，易向对方妥协；他生我，则对方更易妥协。了解这些，有助于在咨询中给出更有针对性的建议。

---

## 第五章 单身与脱单：何时何地遇良人

### 一、单身者的用神逻辑

单身者问「何时脱单」「正缘何时出现」，仍以合干为未来配偶的用神。看合干落宫是否有空亡：若对方明确表示目前没有对象，合干往往落在空亡之宫，表示那个人「尚未出现」。空亡宫所对应的地支月份，往往是对方可能出现的时间点。但若合干宫本身就有四害，即使出现了，感情也不稳定。

六合宫位若空亡或有四害，即使有了对象也不易长久，需提前告知求测人，在心态上有所准备。

### 二、桃花与脱单时机

桃花，即十二长生中的**沐浴**位。日干、合干、年命及下临之干若处桃花，则有婚姻机缘。未婚者遇桃花，魅力增长，易吸引异性。桃花与玄武同宫，可能有风流韵事；桃花与己、辛、壬、癸同宫，需防不检点之事。若相关符号均不带桃花，则多主晚婚。

脱单时间看**桃花落宫**所对应的地支月份，或合干落宫**非填即冲**之时——填实即本宫地支出现，冲实即对宫地支出现。

### 三、对方年龄与距离

年干或年干合干对应的人，偏成熟；日干或日干合干对应的人，偏年轻。差几岁可看落宫：离宫多主大3—6岁，坎宫多主大1—4岁。距离则看落宫远近：内盘为近，外盘为远。

### 四、日时关系的综合判断

日干克时干，格局好则能掌控局面，格局差则被动；时干克日干，多主不顺；日干生时干，主动寻找；日时比和，不太费劲；时干落宫差，则缘分来得麻烦。双方都在内盘，来得快；都在外盘，来得慢。

---

## 第六章 十天干与八神九星的婚恋象意

### 一、十天干在婚姻中的性情表现

甲：庄重威仪、城府深、冷酷。男人是甲易多配偶，女人是甲离婚率高。  
乙：温柔贤惠、柔情似水，妻象，多有艺术气质。  
丙：英武热情、脾气急，重权力与热情。  
丁：精致细腻、洞察力强，带锋芒与希望。  
戊：诚实守信、固执，重钱与平台。  
己：温顺而诡计多端，善策划，有贵气。  
庚：大义凛然、刚健，丈夫象，也主阻碍。  
辛：叛逆偏激，有阴谋感，主变化与错误。  
壬：圆滑多变、聪明却迷茫。  
癸：多愁善感、多情，主性与困境。

### 二、八神反映的恋爱心态

值符：浪漫、气质高雅、有名望。  
腾蛇：反复无常、三心二意、死缠烂打。  
太阴：细腻、暗中喜欢、暗恋。  
六合：合得上、结婚、人缘好。  
白虎：情感炙烈、有话直说。  
玄武：神秘、有私情、善忽悠。  
九地：发展慢、但稳固。  
九天：好高骛远、偏虚幻。

### 三、九星恋爱术：追求策略

根据对方或自己的九星落宫，可采取不同追求策略：天蓬——撒钱、团队合作、风雨无阻；天芮——共同学习、共同兴趣，循序渐进地挑毛病；天冲——冲动表达、快速达成、迎合对方；天任——执着、慢热；天辅——难追，需环境气氛到位再表白，要包容；天英——包装自己，选灯火阑珊处；天柱——情话绵绵，少动多静；天心——难追，需理性说服，偏交易心态。

---

## 第七章 婚姻调整与策划

### 一、催桃花（无对象时）

两宫都无四害时，可选吉时在两宫摆放风水物品。也可把自己用神宫调到**沐浴位**，或把自己宫调至无四害、把合干宫调至生比宫，最佳是合干宫生自己宫。

### 二、催结合（有意中人时）

找与月干（意中人）相合的日子起局，可选烧两道相思和合符。若已确定恋爱关系，用相合之干择日；未确定则用己下临地盘择日。择日时需选日干、日干地盘、两宫皆无四害的时辰摆放风水物。

### 三、催婚姻（有对象时）

无四害可直接放风水物；有四害需先化解再摆放。

### 四、拆小三（结婚后）

配偶落宫若出现乙、丙、丁，对应环境中可能有钉子、花等物品，需拿掉。有时格局中虽无乙丙丁，但现实中存在，也要找到对应物品移除。

### 五、促离婚（结婚后想离）

若两宫有离婚信息、无共同符号、一方空一方不空，可通过在一宫摆放风水物增加能量、破坏同气，或拿掉共同符号来推动分离。

### 六、天蓬天任的妙用

天蓬代表性感，可把自己移到天蓬位置以增强魅力；天任主执著、失去感觉，若想把对象或小三「降温」，可把对方符号移到天任位置。

### 七、策划婚姻（择偶建议）

最宜正配；若不行则挨宫查找，看通气与共同符号。甲己实在不行可考虑阳阳配（丙戊庚）。若全盘无合适宫位，则可能只有精神恋爱，难有肉体姻缘。

---

## 第八章 前夫前妻的取用

若求测人先问现在感情，再问前夫或前妻，则被测人用**天盘翻地盘**来看，因为前配偶已成过去。若直接问前夫前妻，则仍看合干。`
  },
  {
    id: 'career',
    title: '事业财运预测',
    desc: '求职、升迁、开店、投资与商业策划',
    color: 'bg-indigo-600 text-white',
    content: `# 奇门测事业教程

---

## 开篇：事业预测的阴阳思维

事业是安身立命之本。无论是求职、创业、升迁还是求财，人们都渴望在正确的时机做出正确的选择。阴盘奇门在事业预测上有一个鲜明的特点：**不执着于吉凶**。海云先生有言：「假酒就做假酒。」意思是，符号对应什么行业，就往什么行业断，不必因为该宫有凶神就否定——很多时候，所谓的「凶」恰恰对应着高风险高回报的领域，关键看求测人是否适合、是否驾驭得了。

事业预测要回答两大核心问题：第一，**最适合做什么**——这看日干天盘落宫，若用刻盘则看时；第二，**现在最佳选择是什么**——这看时干天盘落宫，刻盘看刻。前者是天赋与长远方向，后者是当下的最优选项。二者未必一致，需结合求测人的实际情况综合判断。

---

## 第一章 取用神与事业成败

### 一、四纲在事业中的对应

当面测时，**日干**为求测人；电话测、网测时，**月干**为求测人。**时干**代表事体、平台、下属、发展趋势，是判断事业成败的「第二用神」。**年干**代表领导、大环境、政策走向；**月干**在职场中既可代表同事，也可代表竞争对手或直接领导。明确这些对应关系，是断准事业局的前提。

### 二、时干：平台与趋势的晴雨表

时干宫的状态，直接反映所求之事的成败趋势。若时干落宫遭遇四害（击刑、门迫、空亡、入墓），则平台有问题、环境不利、发展趋势受阻。反之，时干无四害，说明自身条件好、外部环境相对有利。

时干也代表下属与员工。若问企业管理，时干宫的能量强弱，可反映团队素质与执行力。时干宫与日干宫相生、比合、通气，且有相同符号，则人事和谐，事业有前景。

### 三、日时关系的五种模式

**事生人**：时干宫生日干宫，表示所问之事会推动你前进，机会主动找上门，是最理想的格局之一。  
**人克事**：日干宫克时干宫，表示你能掌控局面，做事顺手，容易成功。  
**人生事**：日干宫生时干宫，表示你需要付出较多努力才能推动事情，属于「事在人为」型。  
**事克人**：时干宫克日干宫，表示压力大、阻力多，做事艰难，需有心理准备。  
**人事比和**：两宫五行相同，表示轻松达成，不太费劲。

### 四、翻宫与职业方向的修正

若日干落宫或所用神落宫遭遇四害，不能直接读该宫符号，而需**翻宫**——把地盘翻到天盘，看转移后的宫位。翻宫后的符号，才是真正的职业方向或最佳选择。若翻宫后仍有问题，可退而求其次，看九宫中哪一宫状态最佳，选其对应的方向或行业。

---

## 第二章 职业方向与取像直读

### 一、宫位符号即行业密码

阴盘奇门的核心逻辑是**象意读解**。日干和用神落宫若无四害，则该宫的符号组合——天干、九星、八门、八神——直接对应求测人适合的行业与职业。十二长生的旺衰，则反映其在该领域的能力强弱。

例如，日干落震宫，临天冲星、伤门、庚，可能适合与车船、军警、竞技、讨债相关的行业；落巽宫临天辅星、杜门、乙，可能适合文化、教育、设计、科技。符号的组合千变万化，需熟记各符号的行业象意，再灵活拼接。

### 二、九星与行业的深层对应

**天蓬星**：胆大妄为、敢于冒险，对应风险生意、偏门、水产品、餐饮、洗浴、歌舞厅等。  
**天任星**：任劳任怨、敦厚老实，对应土地、农产品、房地产、矿山、山林交易。  
**天冲星**：雷厉风行、直来直去，对应电器、花店、游乐场、山林野味、鞭炮、车船运输。  
**天辅星**：儒雅有文化，对应文化卫生用品、工艺设计、科技攻关、木材行业。  
**天英星**：注重形象、性急，对应电子电器、电视音响、美容美发、演艺。  
**天芮星**：有问题意识、好学习，对应医疗、学校、棉麻、肉类加工。  
**天柱星**：能说会道、中流砥柱，对应娱乐、说教、中介、歌手。  
**天心星**：工于心计、善管理，对应医药、组织策划、管理。

### 三、八门与职业的对应

休门主贵人扶持、水类、休闲娱乐、美容修理；生门主生意、养殖、房地产、新机会；伤门主车船、司机、军警、讨债、赌博；杜门主技术、保密、公检法、科技；景门主美容、影视、广告、通信、礼仪；死门主神佛、肉类、屠宰、地皮；惊门主口舌说唱、饭店、娱乐、律师；开门主工厂、店铺、五金、政府机构。

### 四、五行与行业大类

见火多（丙丁、天英、景门等），可选能源、化工、照明、影视、电子、互联网；见水多（壬癸、天蓬、休门等），可选物流、水产、运输、旅游、饮料、酒业；见金多（庚辛、天心天柱、开门惊门等），可选金融、机械、金属、财政；见木多，可选文化、种植、中药、艺术；见土多，可选房地产、中介、策划、农业。

---

## 第三章 求财预测的完整体系

### 一、求财的核心符号

**日干**为人、求测人；**时干**为财、产品、顾客、员工；**戊**为资本、现金、投资；**生门**为利润、利息；**值符**为信贷、银行、债主、货主；**直使**为购货人；**六合**为中间人、中介；**月干**为同行、竞争对手。这些符号在不同求财场景中扮演不同角色，需根据具体问题灵活选用。

### 二、戊的三层含义

**天盘戊**代表正在投入或流动的现金、投资款；**地盘戊**代表已经落袋为安、放在口袋里的钱；**引干戊**代表能借到的钱、能为你所用的外部资金。看一个人有没有钱、能不能借到钱，要分清楚这三个层次。

### 三、财运判断四步法

第一步，看**戊**——资本状况。戊落宫的旺相休囚、十二长生、四害、格局，决定了资金是否充足、能否到位。戊落空亡，钱到不了；戊落坤宫入墓，有财也动不了。

第二步，看**生门**——利润。生门与戊的关系、生门自身的状态，决定能否获利、获利多少。

第三步，看**被日干克的正财**——长期事业财运；**被日干克的偏财**——短期财运、偏门收入。

第四步，看**时干平台**与用神的关系。时干宫生用神，平台有利；时干宫克用神，平台不利。

### 四、坐地求财：开店的判断

开店以**开门**为用神。日干为求测人，时干为产品、顾客、员工，月干为同行竞争对手，景门为市场行情。

开门临**空亡**：若店还未开，则开不成；若已开，则可能已关闭或即将关闭。  
开门临**玄武**：产品质量有问题，或内部有人暗中搞鬼。  
开门临**值符**：有领导或贵人支持，容易成功。  
开门临**腾蛇**：时好时坏，管理混乱，不稳定。  
开门临**白虎**：阻力大、压力大，经营有难度，需防意外伤灾。  
开门临**九地**：平稳但发展慢，不愿冒险，易停滞不前。  
开门临**九天**：前景广阔、声名远扬，但需防好高骛远、目标脱离实际。

---

## 第四章 人物关系与升官预测

### 一、职场中的四柱对应

在事业预测中，年干代表大领导、国家政策、经济环境；月干代表同事、同行、竞争对手，有时也是直接领导；日干代表自己；时干代表下属、员工、单位平台。看升迁，必看日干与年干、月干、时干的生克关系。

### 二、上下级关系的理想状态

**年干生日干**：领导对自己满意，会支持工作，政治环境和谐。  
**月干与日干相生或比和**：与直接领导、同事相处融洽，无人打小报告。  
**时干生日干**：下属拥戴，群众基础好。

若日干与年干相生，且两宫有**共同符号**，则上下级水乳交融、配合默契，位置坐得稳，上升空间大。反之，若年干克日干，或月干、时干冲克日干，则需防小人、阻力、降职。

### 三、小人的识别

月干或时干临**玄武**冲克日干，主单位内部有小人作祟，易引发口舌官非。谁克你、冲你，谁就是小人。腾蛇相克主难缠；白虎相冲克主对方气势汹汹、明目张胆；太阴相冲克主暗中行事、不知不觉；六合或寄宫冲克日干，主合伙聚众为难自己。

### 四、升官的条件与罢官的信号

用神临**直符、九天、太阴、丙、庚、杜**且能量大（十二长生旺），主能做大官；能量小则官职小。升官看直符、太阴、九天；罢官看能量损失与四纲抑制。

用神临**马星、壬、癸、生门、九天**，主必动、有升迁之喜。若自己和时干都没有这些符号，即使宫位好也不易升。

**六甲伏吟**主新局面刚刚开始；**反吟**主反复、不稳定、调任。日干或用神**空亡**，主离职、退休、革职。日干或用神遇**门迫、击刑、入墓**，主能力发挥不出来、处境别扭。

---

## 第五章 五行与企业策划（王凤麟体系）

王凤麟先生将五行引申为企业五种力：**金**代表核心竞争力、绝活；**水**代表应变能力、创造力；**木**代表增长力、势能、变现能力；**火**代表企业文化、形象、个人品牌、影响力；**土**代表综合实力与整合力。看企业或个人的事业格局，可从其用神落宫的五行属性，推断其强项与短板。

---

## 第六章 八神在事业中的象意

**值符**：产品临值符为稀有、名贵、高档、货真价实；企业临值符有背景、有靠山。  
**腾蛇**：产品临腾蛇不稳定、变来变去；企业临腾蛇一起一伏、瞻前顾后。  
**太阴**：产品临太阴为细腻、精雕细琢；企业临太阴策划能力强、管理细致。  
**六合**：产品临六合为人见人爱、多种产品；企业临六合善于合作、联合经营。  
**白虎**：产品临白虎技术过硬；企业临白虎强势、厉害。  
**玄武**：产品临玄武为高技术、难模仿；企业临玄武鬼点子多，但也易上当。  
**九地**：产品临九地稳定、经济耐用；企业临九地行动迟缓、保守。  
**九天**：产品临九天高端、高价、富于变化；企业临九天志向远大，但也可能好高骛远。

---

## 第七章 交易、合伙与投资

### 一、交易求财

日干为买主（或我方），时干为卖主（或对方），六合为中介。六合生谁，中介就偏向谁。日干生时干，我受制于对方；时干克日干，对方不卖；日干克时干，买主不要；日时比和，公平交易。若有一方空亡，或临玄武、反吟、入墓，交易多不成。

### 二、合伙求财

日干为我，时干为合伙人。时干临吉神吉格生我，利我；时干冲克我，不利。

### 三、买货与卖货

买货时，日干为买主，时干为货物。时干宫旺得吉神吉格，货物质量好；上乘玄武腾蛇，可能假冒伪劣。卖货时，值符为卖方，直使为买方。使生符或使克符可卖出；符生使或符克使则卖不出。

### 四、投资与贷款

投资看戊与生门。戊落空钱到不了；戊落坤入墓有财也动不了。生门生戊且得吉神吉格，必获利；生门克戊又逢凶神凶格，必亏本。

贷款时，值符为银行或物主，直使为借贷人。符生使或使克符可贷到；使生符或符克使，或有一方空亡，借贷不遂。

---

## 第八章 毕业分配与官运调理

毕业分配看用神落宫好坏。用神好、得天时地利、时干相生，分配顺利；日干克时干，通过努力可达目的；不得天时地利又时干相克，则不顺利。

官运调理的思路是：使用神临直符、太阴、九天、马星，或用神挪至四纲之上，或通过风水布局催发。`
  },
  {
    id: 'health',
    title: '疾病健康预测',
    desc: '七步断病法、天干脏腑象意与疾病调理',
    color: 'bg-emerald-600 text-white',
    content: `# 奇门测疾病教程

---

## 开篇：疾病预测的逻辑与局限

人吃五谷杂粮，没有不得病的。奇门断病的逻辑，是通过用神所在九宫的天干、九星、八门、八神组合，分析被预测者的身体状况，再结合万象相干论、万象全息论等原理，从环境调理入手，解除不良信息场的组合，从而辅助改善健康状况。

需注意：**没有结过婚的人难以测准婚姻，没有买过房的人难当买房军师**。同理，疾病预测需要一定的医学知识储备，才能将符号转化为合理的健康建议。奇门是「象」的学问，象意需要与现实对接，否则易流于臆测。

---

## 第一章 取用神与预测流程

### 一、取用神

面测以日干或年命落宫为主，兼看四纲宫；电函测以月干或年命落宫为主。四纲以外宫位有问题，多主亚健康或隐性症状；四纲宫有问题，则病症明显。

### 二、预测七步法

第一步，选用神，找出求测人落宫。  
第二步，看四害——能量高低是判断病势轻重的关键。  
第三步，分析用神宫符号组合，结合天干脏腑、八门病态、九星特性、八神病因。  
第四步，看伏吟、反吟——伏吟主慢性、稳定，反吟主急性、反复。  
第五步，看马星——临之则病愈快或死亡快，变化迅速。  
第六步，分析病期：非冲即填。  
第七步，分析调理：移星换斗、行为风水、环境调理。

---

## 第二章 天干人体象意

### 一、体表口诀（易学通用）

「甲头乙肩丙为额，丁齿戊鼻己为面。庚筋辛肋壬为胫，癸为双足是真踪。」  
「甲头乙颈丙肩求，丁胸戊肚己脐腹。庚为腰间辛为肋，壬是股部癸四肢。」

这两套口诀在不同流派中略有差异，可结合使用。核心思路是：甲为头领、乙为弯曲关节、丙为肩、丁为齿与胸、戊为鼻与肚、己为面与脐、庚为筋与腰、辛为肋、壬为胫与股、癸为足与四肢。

### 二、脏腑口诀（易学通用）

「甲肝乙胆丙小肠，丁心戊胃己脾乡，庚是大肠辛属肺，壬系膀胱癸肾藏。三焦亦向壬中寄，包络同归入癸乡。」

脏腑与天干的对应，源自五行与脏象学说。断病时需将天干落宫与宫位对应的人体部位结合，再对照脏腑口诀，综合判断病位。

### 三、测疾病常用对应（简化）

甲主筋骨、头；乙主血管神经、西医；丙主血液；丁主骨刺、粉刺；戊主肉、肿；己主经络等；庚主大骨头、肿瘤；辛主小骨头；壬主肾、动脉；癸主静脉、痣。癸为痣，壬为胎记，需看四害及与日干作用。

---

## 第三章 八门、九星、八神的疾病象意

### 一、八门与疾病状态

**杜门**：隐藏，没症状。  
**休门**：修复期、调理、休眠或死亡。  
**伤门**：损害、受伤。  
**生门**：发展期。  
**景门**：发热、烧伤、烫伤。  
**死门**：功能丧失、不发展、死亡。**死门+庚**多为癌症，庚为瘤。  
**惊门**：阵痛、抽搐、咳喘。  
**开门**：开放性伤口。**开门+丁辛**多主手术开刀。

### 二、九星与疾病特性

**天辅**：传染性病。**天任**：发展速度慢。**天蓬**：善于变化。**天芮**：病根、病灶。**天心**：西医、医药。天芮+死门可能对应佛像、龙凤画、观音等（灵异类病因）；天芮+伤门可能对应关公等武神类。

### 三、八神与病因

**值符**：神佛。**太阴**：太阴法术；太阴+惊门主诅咒念咒，+景门主烧符，+开门主开坛做法，+伤门主斗法，+杜门主暗地做法。**腾蛇**：腾蛇+癸主灵异冤亲债主；+木柳仙蟒仙，+火狐仙，+土黄仙，+金白仙刺猬，+水灰仙老鼠；+死门主巫婆神汉。**白虎**：技术一流大夫、做手术。**死门**：过去存在的人、祖坟。**空亡**：转移。**击刑**：不舒服、别扭、痛。**门迫**：破损、肢体伤害、脏腑坏死。**伏吟**：久病、慢性病。**反吟**：对宫天地盘相反，来回折腾。

---

## 第四章 天干落九宫疾病象意（王凤麟体系）

王凤麟体系将十天干落在九宫（乾坎艮震巽离坤兑）中，与宫位对应的人体部位结合，形成详尽的组合象意。例如乙落乾宫主右脚或右腿的神经血管，乙+丙主腿部炎症，乙+庚主腿部硬化坏死；丙落坎宫主外生殖器，丙+丙主炎症红肿；戊落艮宫主胃，戊+戊主胃瘤或胃动力不足……《奇门遁甲预测疾病》中有完整的「天干落九宫象意」表，可逐一查阅。此处不赘述，学者须熟读原典。

---

## 第五章 疾病预测五步法与关键断语

### 一、五步法

取用神→分析用神（能量、四害、芮+心+乙可治等）→看时干、太岁、天芮、马星→判断治疗方案（乙奇、丁奇、天心 vs 天芮）→分析病期与调理。

### 二、关键断语

凡临四害能量降低之宫，均为有病；四纲宫受损再临芮必有病。**引干是病的起因**，断病先从引干入手。疾病根源在**四纲宫的先天宫受损**。用神临乙、心，治病有方。用神能量低且临白+庚、杜+庚、白+杜，为顽症。伤+开、开+辛+丁，多主动手术。用神能量高且芮+马、芮+壬癸、再临震，病愈快。丙丁景主炎症；乙心为医生医药；蛇反吟主病变反复。伏吟九地死门主病情稳定。天+壬癸主血压高，地+壬癸主血压低。丙丁壬癸+冲主脑溢血等。门迫击刑遇空亡，填实或逢冲时力量增大数倍。天芮遇空亡可能病情转移。**找克制天芮星的宫位，即为医治病的方位。**

「久病怕反吟，近病怕伏吟，马星主快慢」——这三句话务必牢记。

---

## 第六章 多宫综合判断

若求测人已知身体某处有问题，单看天芮不够，需结合**用神落宫**和**时干**综合判断。用神与时干都遇四害，说明身体被掏空；仅天芮凶而用神、时干尚可，则疾病虽猛但身体还能支撑，及时调整可化险为夷。

---

## 第七章 疾病调理

移星换斗：对有损的用神宫、先天宫进行能量转移。行为风水：根据宫位符号指导求测人作息、就医方向等。环境调理：拆除或移走致病环境符号对应的物品。引干为药引子，可从引干对应的方位、物品入手调理。`
  },
  {
    id: 'school',
    title: '学业考试预测',
    desc: '学科天干对应、文昌催旺与考试运势',
    color: 'bg-cyan-600 text-white',
    content: `# 奇门测学业教程

---

## 开篇：学业预测的特殊性

学业预测与婚姻、事业不同，它涉及的是「知识」「成绩」「考试」这类抽象事物。阴盘奇门通过**天干与学科的对应**，将抽象转化为可操作的符号体系。例如壬为数学、丁为语文、庚为物理——这种对应并非随意规定，而是源于天干的本质象意：壬为智慧之海、数字之渊；丁为文明之火、文字之光；庚为坚金、不变之理。理解其背后的逻辑，才能灵活运用。

另外，学业调理有一个容易被忽视的要点：**高考等重大考试，仅调日干时干不够，必须同时调理正印与正官**。日干时干决定平时状态与基础成绩，正印决定临场发挥与贵人运，正官决定考试通过的运势。四者齐备，方能创造「超常发挥」的可能。

---

## 第一章 取用神与问测场景

当面问测自己学业，以**日干落宫**为代表；电话问测，以**月干落宫**为代表。代他人问测时：代兄弟姐妹问取月干，代子女问取时干，代父母或长辈问取年干。这一规则与婚姻、事业预测一致，核心是「谁在问事，谁为用神」。

**日干**代表人，即求测者的学习态度、自身状态；**时干**代表事，即学业发展趋势、学习能力、实际学业水平。看学业，必须两者兼顾：日干好而时干差，可能态度端正但方法不对；时干好而日干差，可能聪明但不用功。

---

## 第二章 学科与天干的对应关系（务必熟记）

**注意：此处天干均指天盘之干。**

| 学科 | 对应天干 | 理由简述 |
|------|----------|----------|
| 语文 | 丁 | 丁为文化、文明，叮叮当当如言语之声 |
| 数学 | 壬 | 壬为数字的海洋、智慧、逻辑与计算；壬越学越迷茫，也代表渊博 |
| 物理 | 庚 | 庚为最坚硬的物质，阻隔、不变，对应物理学的恒定规律 |
| 化学 | 辛 | 辛为变化、革命、创新，对应化学的反应与转化 |
| 历史 | 己 | 己为过去的、老旧的，与历史之「往昔」相应 |
| 心理学 | 己 | 同上，己主策划、心思，与心理活动相关 |
| 经济 | 戊 | 戊为土、为钱，古代土代表财货 |
| 地理 | 戊 | 戊为大地、平台，与地理之「地」相应 |
| 政治 | 值符、丙 | 值符与丙均为权力；值符落他干时首选丙 |
| 生物 | 癸 | 癸为精血、生命之源，与生物之「生」相应 |
| 艺术/美术 | 乙 | 乙为柔美、艺术、花草 |
| 英语/外语 | 庚辛落引干、或天英星 | 庚辛为金、为西方；天英为外、为华丽；引干主隐藏之外语能力 |
| 体育 | 马星 | 马星主动、主奔跑、主竞技 |

---

## 第三章 成绩判断的逻辑

将学科对应天干的落宫，与求测者（日干或月干）的落宫进行比较。**生、比**：该科状态好，优势明显。**我克**：需主动努力才能提升。**克我**：压力大、状态不佳、易遇阻碍。**我生**：需消耗大量精力才能维持或提升，易疲惫。

**求测者落宫（本宫）是主基调**。若本宫状态差，整体学业会受牵连，单科再好也只是相对优势。调理时，务必先稳固本宫，再针对薄弱学科。

---

## 第五章 催文昌的方法

### 一、奇门催文昌

用**终身局**来布局。文曲星在奇门中对应**天辅星**。若天辅星落宫有四害，须先化解四害；无四害则直接在该宫摆放文昌塔、毛笔、印绶等风水物。

### 二、玄空催文昌

固定文昌位根据房屋座山起玄空飞星盘，找飞星组合14、41、16、61、49、94、39、93（四绿文曲与一白文昌同宫，或三九、四九木火通明）。其中49、94组合力量较强。流年文昌位则根据当年飞星找四绿所在方位。

### 三、学业策划案例要点

若某科成绩差，可检查该科对应天干所在宫位是否有不利物品。例如历史差，戊所在宫位可能有墙、柜子等阻碍物，可考虑移除或改造；化学差，辛所在宫位可能有金属、颗粒状物品干扰；语文差，丁所在宫位可能有灯、电器等过强火象。坎宫若空亡，可填实以增强根基。

---

## 第六章 天赋判断：手相面相的辅助

额头宽广主思维灵活，凹陷或有痣主读书吃力；耳朵高过眉毛主分析力强、成绩好；眉毛浓主责任心、恒心，淡主耐心不足，不齐主心乱心散。手相智慧线长而深主学业好、专注，短而乱主成绩一般，多小线条主爱好多、才能多，很短主学历受限。天赋不足可从内因（培养、苦读）与外因（风水、法术）两方面补救。

---

## 第七章 调理要点汇总

日干、时干为基础盘；正印、正官为考试突破关键；学科用神宫为针对性调理；天辅星为文昌位布局；四害处理优先于摆放。调理顺序建议：先化解四害，再摆放助益物品，最后根据学科符号做微调。`
  },
  {
    id: 'fengshui',
    title: '阳宅风水调理',
    desc: '外景形煞、内景布局与催财化煞',
    color: 'bg-amber-600 text-white',
    content: `# 奇门测风水教程

---

## 开篇：阴盘奇门的三个层次

古人云：「翠竹黄须白笋芽，儒冠道履白莲花。红花白藕青荷叶，三教原本是一家。」阴盘奇门同样有三个层次：**天气奇门**以天时起局，主预测与调整；**地气奇门**以山向起局，主风水改向；**神气奇门**以宇宙能量为宗旨，涉及法术、咒语、步法。测风水、调风水，主要运用地气奇门，但天气奇门可配合择时，神气奇门可配合符咒，三者相辅相成。

---

## 第一章 风水学基础

### 一、风水的本质

郭璞《葬书》云：「气乘风则散，界水则止。古人聚之使不散，行之使有止，故谓之风水。」风水学研究的是气的聚散与运行。上等风水为有水之地，其次为藏风之地。避风聚水，阳宅阴宅方能得生气，居住者或后代方能兴旺。

风水学又称堪舆学——「堪」为天道、高处，「舆」为地道、低处，合起来即研究天地之道的学问。流派上，形势派重峦头形法，理气派重八宅、飞星、奇门等。形式为体，理气为用，二者缺一不可。

### 二、常用名词速览

**龙脉**：山脉走向、河流弯曲、街道伸展，非真龙，而是比喻。  
**穴**：气流聚集之处。  
**砂**：穴位周围的高处；室内，沙发、书架等高于平面的皆为砂。  
**水**：江河湖海、浴缸、厕所等低于平面之处。  
**明堂**：穴前开阔平坦的空间。  
**向**：坐向。

---

## 第二章 山向奇门与定局

### 一、定局原则

山向奇门用**日柱时柱**起局，以**第一层子午**定向。形势（周围环境）与理气（三元九运、建造年份起玄空飞星盘）需结合使用。预测局看哪年搬进，调整局用当前时间。用局规则：亥山到巽山用阳局，巳山到乾山用阴局，需根据罗盘山向查表确定。

### 二、阳宅用神（断局关键）

人在面前、房在面前：**日干**代表房子和求测人。  
人在面前、不在房前：**正印**为房。  
求测人不在、策划师在房前：**月干**为用神。  
人不在、房也不在：日干为预测师，月干为求测人，**月干正印**为房。

---

## 第三章 外景风水象意（王凤麟体系）

现代环境比古代复杂，外景形煞需结合符号组合判断。以下为常见组合：

**丁+戊、白虎**：墙角射来。  
**丁+庚、天冲**：大路冲射。  
**丁、天蓬、冲克**：房檐冲射。  
**丁+戊、天任、九地、冲克**：地脚线冲射。  
**丙丁、腾蛇、冲克**：光煞。  
**庚、白虎、冲克**：白虎煞。  
**白虎、开门、天柱**：天斩煞。  
**庚辛冲克**：大路小路冲。  
**壬癸冲克**：水冲。  
**门迫之宫**：物破。  
**宫位见辛**：必有破。  
**玄武**：探头煞。  
**腾蛇、丙丁、天柱**：电杆、电塔。  
**甲乙、天柱**：大树。  
**丁+壬、天柱**：旗杆。  
**庚辛落食神宫**：反弓路。  
**壬癸落食神宫**：反弓水。  
**庚辛空亡**：路断。  
**壬癸空亡**：水断。

八卦宫位与人体、方位对应：乾西北主头颈肺大肠，坎北主肾膀胱生殖，艮东北主背手胃鼻，震东主足肝胆，巽东南主股气管神经，离南主眼心小肠，坤西南主腹脾胃，兑西主口肺气管。外景有问题时，可结合宫位推断对居住者身体的影响。

---

## 第四章 内景风水象意

### 一、功能区域与符号

**床**：休门、乙、桃花。  
**门**：值使门、马星、壬癸。  
**浴室/洗手间**：壬癸、惊门、桃花。  
**厕所**：己、癸、死门、桃花。  
**厨房炉灶**：丙丁、腾蛇、生门。  
**餐厅/餐桌**：己+乙、景门。  
**客厅**：六合、乙、戊、天心。

### 二、物品象意

**水管**：壬癸乙己庚辛；遇丙丁为热水；乙主弯曲，己主盘旋。  
**横梁**：戊、庚、甲（直符）。  
**灯**：丙丁、天英、景门；丁+癸+蛇主忽明忽暗。  
**家具/床**：乙、天辅。  
**神佛**：死门、值符、天英、遗像、玩偶；天芮也主神佛、书籍。  
**窗**：庚辛主阻挡，丙丁主采光。  
**图画照片**：景门、玄武。  
**电器音响**：丙丁、惊门。

---

## 第五章 定财位与催财

### 一、财位确立

可结合玄空、奇门、八宅。八字日柱天干地支所克者为财；日柱天干地支所属卦向也为财位。禄位可当财位：甲禄寅、乙禄卯、丙戊禄巳、丁己禄午、庚禄申、辛禄酉、壬禄亥、癸禄子。

### 二、奇门催财取宫

日干宫、年命宫、生门宫、戊天盘、戊地盘，取无四害、与行业相关、月令当旺之宫。静财用貔貅、关公等；动财用风水轮、养鱼、烧香，动财大于静财。

---

## 第六章 调理方案：弃、改、放

**弃**：环境太差，不改了，放弃或转让。  
**改**：改山向、改门窗、放风水物、室外建形势。五黄大煞可用火（如红地毯）通化。  
**放**：根据落宫象意选择风水物。改向时，人的年命落宫不能有四害，年命最好落在山或向所在宫位；选局不能出卦，一卦管三山；优先本宫，其次相邻两宫。

---

## 第七章 大太极与小太极

**大太极**指整个房屋、外环境；**小太极**指房间内、具体方位。断局时先定大太极，再定小太极，层层细化，才能精准定位问题与调理点。

---

## 第八章 行为风水

除了摆放物品，还可通过**行为**配合风水：拆——净天地咒+符，如戊击刑可折纸飞机飞出破财；补——根据宫位符号做相应动作，如东南用梳子盘头、戴方发卡；移——如兑空亡，一群人从西聊到北。需相应时间、相应符号、相应行为三者配合。`
  },
  {
    id: 'lucky',
    title: '奇门择吉择时',
    desc: '无四害、应象与飞星入斗择日法',
    color: 'bg-purple-600 text-white',
    content: `# 奇门择吉教程

---

## 开篇：择吉的阴阳之道

古人云：「天时不如地利，地利不如人和。」但若天时、地利、人和三者兼备，则无往不利。择吉，正是对「天时」的主动选择。阴盘奇门择吉不拘泥于传统黄历的宜忌，而是从当前起出的奇门局中，直接寻找无四害、与所办之事相生的宫位，取其对应的时间与方位行事。这种方法更加灵活，也更贴合当事人当下的气场。

《奇门时空局》强调：择吉不是简单的挑日子，而是要让**用神落在四纲上**，让所选时空与事体形成共振。只有这样，才能「在天成象，在地成形，在人成运」。

---

## 第一章 择吉的五大核心原则

### 一、宫位无四害是底线

四害——击刑、门迫、空亡、入墓——会严重削弱宫位能量。择吉所选之宫，**绝不能有四害**。否则，纵有吉神吉门，也被抵消大半，择吉效果大打折扣。这是第一道门槛，没有商量余地。

入墓的宫尤其不宜选用，因为入墓表示「被藏起来」「退居二线」，能量难以发挥。择吉要的是「显」「动」「发」，与入墓的意象恰恰相反。

### 二、宫位与预测事相生

所择宫位的五行，应与所办之事的五行相生。例如开业求财，宜选土、金、水相关的宫位（土生金、金生水，财为金水）；结婚择日，宜选与双方年命相生的宫位。相生则助，相克则阻。

### 三、宫位应象

应象，指的是宫位内的符号与所办之事在象意上能够对应。例如开风水课，若选兑宫，兑宫有丙（红衣服）、戊（钱）、天柱、惊门，可解读为「穿红衣服、与口才宣传相关、有收益」——与开课之象吻合。再如开业选离宫，离宫戊、生门、天蓬，可解读为「有钱、利润、大单」——与开业求财之象吻合。应象越强，择吉越精准。

### 四、用神在四纲上

四纲即年月日时四柱。若所选宫位对应的地支，能出现在四纲之中（年支、月支、日支、时支），则用神「上了纲」，能量得以贯通。择吉要求**年月日时至少占两个**，即所择之日、时中，至少有两个与局中吉宫相应。

### 五、避开凶门凶神，首选开休生

白虎、伤门、死门在择吉中多为不利。白虎主阻隔、伤灾；伤门主损伤、竞争；死门主终结、僵化。虽有例外（如选六合结婚、选伤门比武），但一般而言，首选**开门、休门、生门**——开主开创、休主修养、生主生机，皆利于吉事。

---

## 第二章 四正宫与四维宫

杨公风水与六壬均重视**子午卯酉**四正。四正宫（震卯、离午、兑酉、坎子）自旺相，气场稳定，适合行动。四维宫（艮、巽、坤、乾）在奇门中往往更容易遭遇四害，因此择吉时**优先考虑四正宫**：兑、坎、震、巽（巽为辰巳，属四隅，但实践中也常与四正同论）。

若四正宫皆不可用，再退而求四维，但要仔细检查四害。

---

## 第三章 择吉的具体步骤

### 一、宫位筛选

第一步，排除所有有四害的宫位。第二步，排除入墓之宫。第三步，在剩余宫位中，按八神、八门排序：值符 > 太阴 > 九天；开 > 生 > 休。第四步，若八神八门都不理想，则哪个宫相对最好就用哪个。第五步，根据事体内容做应象选择，如结婚选六合落宫、开业选生门戊所在之宫。

### 二、用神宫地支即日时刻

选定宫位后，该宫位所对应的地支，就是可选择的日子、时辰、刻。例如兑宫对应酉，则酉日、酉时、酉刻可用；坎宫对应子，则子日、子时、子刻可用。具体选日还是选时，要看事体需要。重大事项如开业、结婚，最好日、时皆合；一般事项可选时即可。

### 三、填实与冲实的条件

填实，指本宫地支出现在四纲中；冲实，指对宫地支出现在四纲中。择吉时，若用填实或冲实来激活宫位，需要**对冲之宫无四害**，否则填冲之力会被削弱。另外，择吉要求年月日时至少占两个，即不能只选一个地支，而要形成组合。

---

## 第四章 飞星入斗择日（高级技法）

《奇门时空局》中载有飞星入斗择日法，其原理是让**地运**（空间、山向）与**天运**（时间）在洛书数上形成配合，达到天地共振、事半功倍的效果。

### 一、地运与天运

地运来源于罗盘64卦，根据山向（如壬山丙向）查出对应的卦与数。天运即时间，年月日时均可换算为洛书数。当天地之数相合（一六、二七、三八、四九、五十，或合十），则时空切合。

### 二、操作步骤简述

先根据山向查表得地运之数（如2）。再根据洛书口诀找相合之数（如7或8）。然后从当天干支起，找出数字为7或8的日子；从该日子起，找出数字为7或8的时辰；从该时辰起，找出数字为7或8的刻。所得之日、时、刻，即为飞星入斗择出的吉时。此法多用于风水布局、重大仪式，需有一定基础方可操作。

---

## 第五章 化解与布局的时间选择

化解、调理、摆放风水物，同样需要择时。原则是：**所选时间对应的宫位必须无四害**。拆补移操作，选目标宫位地支对应的日、时；放风水物，选放置宫位地支对应的日、时。这样，行为与时空才能同步共振，效果最佳。

---

## 第六章 择吉案例要点

**开业典礼**：选无四害之宫，戊为钱、生门为利润，用神透四纲更佳。  
**风水课开课**：四正宫兑、坎、震、巽若无四害可用。兑宫应象酉日酉时，可穿红衣服、强调口才与收益；坎宫与水有关，可开水产、洗浴类开业；震宫冲+伤，适合竞争类活动，不宜开课。  
**结婚择日**：选六合落宫，六合宫无四害，且与双方年命相生。

**注意事项**：入墓不选；择日、择时、择方需三位一体；有白虎但丙可压制时可酌情使用；下午阳气渐衰，开课若选下午，可考虑酉时集合或报道，以酉金收摄之气作结。`
  }
];
